/**
 * Root Route
 * Provides global layout and providers
 */
import { createRootRouteWithContext, Outlet, Link, type ErrorComponentProps } from "@tanstack/react-router";
import { ConvexReactClient } from "convex/react";
import { useTranslation } from "react-i18next";
import { TooltipProvider, ToastProvider, Toaster, Button, ErrorFallback } from "@jourfx/ui";

interface RouterContext {
  convex: ConvexReactClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: RouteErrorComponent,
});

function RootComponent() {
  return (
    <ToastProvider>
      <TooltipProvider>
        <main id="main-content">
          <Outlet />
        </main>
        <Toaster />
      </TooltipProvider>
    </ToastProvider>
  );
}

function NotFoundComponent() {
  const { t } = useTranslation("common");
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">{t("notFound.title")}</h1>
        <p className="text-gray-600 mb-6">{t("notFound.message")}</p>
        <Link to="/dashboard">
          <Button>{t("notFound.backToDashboard")}</Button>
        </Link>
      </div>
    </div>
  );
}

function RouteErrorComponent({ error }: ErrorComponentProps) {
  return <ErrorFallback error={error instanceof Error ? error : new Error(String(error))} />;
}
